<template>
  <section class="receive-rule">
    <div class="receive-rule__header">
      <h2>{{ $t('account_setting.receive_rule') }}</h2>
      <el-button size="small" type="primary" @click="$emit('create')">
        {{ $t('account_setting.receive_rule.new') }}
      </el-button>
    </div>

    <el-table
        :data="rules"
        v-loading="loading"
        :header-cell-style="headerStyle"
        class="receive-rule__table"
    >
      <el-table-column type="selection" width="52" />
      <el-table-column :label="$t('account_setting.receive_rule')">
        <template #default="{ row, $index }">
          <el-popover
              effect="dark"
              placement="top-start"
              trigger="hover"
              :disabled="!isRuleDescriptionOverflow(row)"
              :width="'max-content'"
              :popper-style="{ maxWidth: '300px', padding: '0' }"
          >
            <div class="receive-rule__description-popover">
              {{ getRuleDescription(row) }}
            </div>
            <template #reference>
              <span
                  :ref="`ruleDescription-${$index}`"
                  class="receive-rule__description"
              >
                {{ getRuleDescription(row) }}
              </span>
            </template>
          </el-popover>
        </template>
      </el-table-column>
      <el-table-column :label="$t('account_setting.receive_rule.status')" width="180">
        <template #default="{ row }">
          {{ row.enabled ? $t('account_setting.receive_rule.enabled') : $t('account_setting.receive_rule.disabled') }}
        </template>
      </el-table-column>
      <el-table-column :label="$t('common.operate')" width="200" fixed="right">
        <template #default="{ row }">
          <el-button link type="primary" @click="$emit('edit', row)">
            {{ $t('common.edit') }}
          </el-button>
          <el-button
              link
              type="primary"
              :loading="isStatusChanging(row)"
              :disabled="isStatusChanging(row)"
              @click="$emit('toggle-status', row)"
          >
            {{ row.enabled ? $t('account_setting.receive_rule.disable') : $t('account_setting.receive_rule.enable') }}
          </el-button>
          <el-popconfirm
              placement="top"
              :hide-icon="true"
              popper-class="receive-rule__delete-popconfirm"
              :title="$t('account_setting.receive_rule.delete_confirm')"
              :confirm-button-text="$t('common.confirm')"
              :cancel-button-text="$t('common.cancel')"
              @confirm="$emit('delete', row)"
          >
            <template #reference>
              <el-button link type="danger">
                {{ $t('common.delete') }}
              </el-button>
            </template>
          </el-popconfirm>
        </template>
      </el-table-column>
      <template #empty>
        <el-empty :description="$t('account_setting.receive_rule.empty')" :image-size="72" />
      </template>
    </el-table>

    <div v-if="total" class="receive-rule__pagination">
      <el-pagination
          size="small"
          background
          layout="total, prev, pager, next"
          :current-page="pageNo"
          :page-size="pageSize"
          :total="total"
          @current-change="handleCurrentChange"
      />
    </div>
  </section>
</template>

<script>
export default {
  name: 'ReceiveRule',
  props: {
    rules: {
      type: Array,
      default: () => []
    },
    folderOptions: {
      type: Array,
      default: () => []
    },
    pageNo: {
      type: Number,
      default: 1
    },
    pageSize: {
      type: Number,
      default: 10
    },
    total: {
      type: Number,
      default: 0
    },
    loading: {
      type: Boolean,
      default: false
    },
    statusChangingIds: {
      type: Array,
      default: () => []
    }
  },
  emits: ['create', 'edit', 'toggle-status', 'delete', 'page-change'],
  data() {
    return {
      overflowedRuleDescriptions: new Set(),
      ruleDescriptionResizeObserver: null
    }
  },
  computed: {
    headerStyle() {
      return {
        textAlign: 'left',
        background: '#f9f9f9',
        color: '#303133',
        padding: '8px 0'
      }
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.updateRuleDescriptionOverflow()
      if (typeof ResizeObserver !== 'undefined') {
        this.ruleDescriptionResizeObserver = new ResizeObserver(() => {
          this.updateRuleDescriptionOverflow()
        })
        this.ruleDescriptionResizeObserver.observe(this.$el)
      }
    })
  },
  updated() {
    this.$nextTick(() => {
      this.updateRuleDescriptionOverflow()
    })
  },
  beforeUnmount() {
    this.ruleDescriptionResizeObserver?.disconnect()
  },
  methods: {
    handleCurrentChange(pageNo) {
      if (pageNo !== this.pageNo) {
        this.$emit('page-change', {pageNo, pageSize: this.pageSize})
      }
    },
    isStatusChanging(rule) {
      return this.statusChangingIds.includes(String(rule?.id));
    },
    isRuleDescriptionOverflow(row) {
      return this.overflowedRuleDescriptions.has(row)
    },
    updateRuleDescriptionOverflow() {
      const overflowedRuleDescriptions = new Set()

      this.rules.forEach((row, index) => {
        const element = this.$refs[`ruleDescription-${index}`]
        if (element && element.scrollWidth > element.clientWidth) {
          overflowedRuleDescriptions.add(row)
        }
      })

      const hasChanged = overflowedRuleDescriptions.size !== this.overflowedRuleDescriptions.size
          || [...overflowedRuleDescriptions].some(row => !this.overflowedRuleDescriptions.has(row))
      if (hasChanged) {
        this.overflowedRuleDescriptions = overflowedRuleDescriptions
      }
    },
    getFolderName(value) {
      const folder = this.folderOptions.find(item => String(item.value) === String(value))
      return folder ? folder.label : this.$t('account_setting.receive_rule.folder')
    },
    getFieldLabel(value) {
      const labels = {
        sender: 'sender',
        senderDomain: 'sender_domain',
        recipient: 'recipient',
        cc: 'cc',
        subject: 'subject',
        body: 'body',
        hasAttachment: 'attachment',
        attachmentName: 'attachment_name',
        isArchived: 'archived',
        archivedCustomer: 'archived_customer',
        archivedContact: 'archived_contact',
        customerLevel: 'customer_level',
        customerSource: 'customer_source',
        intentionProduct: 'intention_product'
      }
      if (value === 'subject') {
        return this.$t('common.subject')
      }
      return this.$t(`account_setting.receive_rule.${labels[value] || 'sender'}`)
    },
    getOperatorLabel(value) {
      const labels = {
        contains: 'contains',
        notContains: 'not_contains',
        equals: 'equals',
        yes: 'yes',
        no: 'no'
      }
      return this.$t(`account_setting.receive_rule.${labels[value] || 'contains'}`)
    },
    getConditionValues(condition) {
      const source = Array.isArray(condition?.values) && condition.values.length
          ? condition.values
          : String(condition?.value || '').split(/[,，;；\n]+/)
      return source.map(item => String(item || '').trim()).filter(Boolean)
    },
    getConditionDetailContents(condition) {
      const source = Array.isArray(condition?.detailContent)
          ? condition.detailContent
          : String(condition?.detailContent ?? '').split(/[,，;；\n]+/)
      return source.map(item => String(item ?? '').trim())
    },
    getConditionDisplayValues(condition) {
      const values = this.getConditionValues(condition)
      const options = Array.isArray(condition?.valueOptions) ? condition.valueOptions : []
      const detailContents = this.getConditionDetailContents(condition)
      return values.map((value, index) => {
        const option = options.find(item => String(item?.value ?? item?.id) === value)
        return option?.label || detailContents[index] || value
      })
    },
    getConditionDescription(condition) {
      const field = this.getFieldLabel(condition.field)
      const operator = this.getOperatorLabel(condition.operator)
      if (condition.field === 'hasAttachment' || condition.field === 'isArchived') {
        return `${field}${operator}`
      }
      return `${field}${operator}${this.getConditionDisplayValues(condition).join(', ')}`
    },
    getActionDescription(rule) {
      if (rule.action === 'mark') {
        const labels = {
          starred: this.$t('account_setting.receive_rule.starred_mail'),
          read: this.$t('account_setting.receive_rule.read_mail')
        }
        const values = Array.isArray(rule.markActions) ? rule.markActions : []
        return `${this.$t('account_setting.receive_rule.mark_as')}${values.map(item => labels[item]).filter(Boolean).join(', ')}`
      }
      if (rule.action === 'forward') {
        const values = Array.isArray(rule.forwardEmails)
            ? rule.forwardEmails
            : String(rule.forwardEmails || '').split(/[,，;；\n]+/)
        return `${this.$t('account_setting.receive_rule.forward_to')}${values.filter(Boolean).join(', ')}`
      }
      return `${this.$t('account_setting.receive_rule.move_to')}${this.getFolderName(rule.targetFolder)}`
    },
    getRuleDescription(rule) {
      if (rule.ruleName) {
        return rule.ruleName
      }

      const conditions = (rule.conditions || [])
          .map(condition => this.getConditionDescription(condition))
          .join(` ${rule.conditionJoin === 'any' ? this.$t('account_setting.receive_rule.any') : this.$t('account_setting.receive_rule.all')} `)
      return this.$t('account_setting.receive_rule.summary_action', {
        conditions,
        action: this.getActionDescription(rule)
      })
    }
  }
}
</script>

<style lang="less" scoped>
.receive-rule {
  min-height: 520px;
  padding: 10px;
  background: #fff;
}

.receive-rule__header {
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  margin-bottom: 16px;

  h2 {
    margin: 0 0 12px;
    color: #303133;
    font-size: 16px;
    font-weight: 500;
  }
}

.receive-rule__table {
  width: 100%;
}

.receive-rule__description {
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.receive-rule__description-popover {
  box-sizing: border-box;
  max-height: 160px;
  overflow-y: auto;
  padding: 12px;
  overflow-wrap: anywhere;
  white-space: normal;
  line-height: 20px;
}

.receive-rule__pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}

:global(.receive-rule__delete-popconfirm .el-popconfirm__action) {
  display: flex;
  justify-content: center;
}
</style>
